import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../commons/Auth';
import { useAlert } from 'react-alert';
import { useLoading } from '../../../commons/Loading';
import axios from 'axios';
import CondoAgreementsPresentational from './CondoAgreementsPresentational';
import { getSignedUrl, getYousignFile } from '../../../lib/fetch/files';
import { NumberParam, StringParam, withDefault } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const CondoAgreements = (props) => {
  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const { profile, data: condo, assemblyActived } = props;

  const [agreements, setAgreements] = useState([]);
  const [availabilityAgreements, setAvailabilityAgreements] = useState([]);
  const [externalAgreements, setExternalAgreements] = useState([]);
  const [externalAvailabilityAgreements, setExternalAvailabilityAgreements] =
    useState([]);
  const [energyContracts, setEnergyContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useQueryParams({
    year: withDefault(StringParam, `${new Date().getFullYear()}`),
    offset: NumberParam,
  });
  const { year } = query;

  useEffect(() => {
    if (condo._id) {
      fetchAgreements();
      fetchAvailabilityAgreements();
    }
  }, [condo._id, year]);

  useEffect(() => {
    if (condo._id) {
      fetchExternalCondoAgreements();
      fetchExternalAdminAgreements();
      fetchEnergyContracts();
    }
  }, [condo._id, year]);

  function onFilter(queryDiff) {
    if (queryDiff.year !== year) {
      queryDiff.offset = 0;
    }

    setQuery(queryDiff);
  }

  const fetchAgreements = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/agreements/condo/${condo._id}`,
      {
        headers: { Authorization: newToken },
        params: { year },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setAgreements(data.agreements);
    setTotal(+headers['x-total-count']);
  };

  const fetchAvailabilityAgreements = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/agreements/avaiability/${
        condo.matchingAdmin.length > 0
          ? condo.matchingAdmin[0].id
          : condo.admins[condo.admins.length - 1].id
      }`,
      {
        headers: { Authorization: newToken },
        params: { year },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setAvailabilityAgreements(data.agreements);
    setTotal(+headers['x-total-count']);
  };

  const fetchExternalCondoAgreements = async () => {
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/external-agreements/browse`,
      {
        agreementType: 'SUPPLIER_CONDO',
        condoId: condo?._id,
        year,
      },
      {
        headers: { Authorization: newToken },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setExternalAgreements(data.externalAgreements);
    setTotal(+headers['x-total-count']);
  };

  const fetchExternalAdminAgreements = async () => {
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/external-agreements/browse`,
      {
        agreementType: 'SUPPLIER_ADMIN',
        adminId:
          condo.matchingAdmin.length > 0
            ? condo.matchingAdmin[0].id
            : condo.admins[condo.admins.length - 1].id,
        year,
      },
      {
        headers: { Authorization: newToken },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;

    setExternalAvailabilityAgreements(data.externalAgreements);
    setTotal(+headers['x-total-count']);
  };

  const fetchEnergyContracts = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/contracts/condo/${condo._id}/energy-contracts`,
      {
        headers: { Authorization: newToken },
        params: { year },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;

    setEnergyContracts(data.contracts);
  };

  async function handleFileListView(signingRequestId) {
    showLoading();

    const { data, error, headers } = await getYousignFile(signingRequestId);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }
    hideLoading();

    const file = new Blob([data], { type: headers['content-type'] });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, '_blank');
  }

  async function handleFileListViewExternal(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  async function handleFileListViewEnergyContract(key) {
    showLoading();

    const { data, error } = await getSignedUrl(key, newToken);

    if (error) {
      hideLoading();
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    hideLoading();
    window.open(data.url, '_blank');
  }

  return (
    <CondoAgreementsPresentational
      data={agreements}
      availabilityAgreements={availabilityAgreements}
      externalAgreements={externalAgreements}
      externalAvailabilityAgreements={externalAvailabilityAgreements}
      energyContracts={energyContracts}
      total={total}
      profile={profile}
      condo={condo}
      handleFileListView={handleFileListView}
      fetchAgreements={fetchAgreements}
      fetchAvailabilityAgreements={fetchAvailabilityAgreements}
      assemblyActived={assemblyActived}
      handleFileListViewExternal={handleFileListViewExternal}
      handleFileListViewEnergyContract={handleFileListViewEnergyContract}
      onFilter={onFilter}
      query={query}
    />
  );
};

export default CondoAgreements;
